import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MethodologyPhase from './components/MethodologyPhase';
import MethodologyConfigurator from './components/MethodologyConfigurator';
import BlueprintDownload from './components/BlueprintDownload';
import CaseStudyCallout from './components/CaseStudyCallout';
import TechnicalTestimonial from './components/TechnicalTestimonial';

const AICoreBlueprint = () => {
  const [activePhase, setActivePhase] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const methodologyPhases = [
    {
      title: "Assessment & Discovery",
      duration: "2-4 weeks",
      description: "Comprehensive evaluation of your current AI readiness, infrastructure capabilities, and strategic objectives. We identify opportunities, assess risks, and establish baseline metrics for transformation success.",
      subPhases: [
        {
          name: "AI Readiness Audit",
          duration: "1 week",
          description: "Evaluate existing data infrastructure, technical capabilities, and organizational readiness for AI adoption.",
          activities: ["Data quality assessment", "Infrastructure audit", "Skills gap analysis", "Stakeholder interviews"]
        },
        {
          name: "Strategic Alignment",
          duration: "1 week", 
          description: "Align AI initiatives with business objectives and identify high-impact use cases for implementation.",
          activities: ["Business case development", "Use case prioritization", "ROI modeling", "Risk assessment"]
        },
        {
          name: "Technology Landscape",
          duration: "1-2 weeks",
          description: "Map current technology stack and identify integration points for AI implementation.",
          activities: ["System integration analysis", "API assessment", "Security evaluation", "Compliance review"]
        }
      ],
      deliverables: [
        {
          name: "AI Readiness Report",
          description: "Comprehensive assessment of organizational AI capabilities and gaps"
        },
        {
          name: "Strategic Roadmap",
          description: "Prioritized implementation plan aligned with business objectives"
        },
        {
          name: "Technology Architecture",
          description: "Detailed technical requirements and integration specifications"
        },
        {
          name: "ROI Projections",
          description: "Financial models and expected return calculations"
        }
      ],
      metrics: [
        {
          value: "95%",
          label: "Accuracy Rate",
          description: "Assessment precision"
        },
        {
          value: "2-4x",
          label: "ROI Potential",
          description: "Identified opportunities"
        },
        {
          value: "100%",
          label: "Compliance",
          description: "Regulatory alignment"
        }
      ]
    },
    {
      title: "Architecture & Design",
      duration: "3-6 weeks",
      description: "Design scalable, secure AI architecture using open-source technologies. Create detailed technical specifications, data pipelines, and integration frameworks that ensure vendor independence and long-term sustainability.",
      subPhases: [
        {
          name: "Solution Architecture",
          duration: "2 weeks",
          description: "Design comprehensive AI solution architecture with open-source components and scalability considerations.",
          activities: ["Architecture design", "Component selection", "Scalability planning", "Security framework"]
        },
        {
          name: "Data Pipeline Design",
          duration: "1-2 weeks",
          description: "Create robust data ingestion, processing, and storage pipelines for AI workloads.",
          activities: ["Data flow mapping", "Pipeline architecture", "Storage optimization", "Processing frameworks"]
        },
        {
          name: "Integration Planning",
          duration: "1-2 weeks",
          description: "Plan seamless integration with existing systems and third-party services.",
          activities: ["API design", "Integration patterns", "Middleware planning", "Testing strategies"]
        }
      ],
      deliverables: [
        {
          name: "Technical Architecture",
          description: "Detailed system design with open-source technology stack"
        },
        {
          name: "Data Architecture",
          description: "Comprehensive data pipeline and storage design"
        },
        {
          name: "Integration Specifications",
          description: "API specifications and integration patterns"
        },
        {
          name: "Security Framework",
          description: "Comprehensive security and compliance architecture"
        }
      ],
      metrics: [
        {
          value: "99.9%",
          label: "Uptime Target",
          description: "System reliability"
        },
        {
          value: "50%",
          label: "Cost Reduction",
          description: "vs. proprietary solutions"
        },
        {
          value: "10x",
          label: "Scalability",
          description: "Growth capacity"
        }
      ]
    },
    {
      title: "Implementation & Development",
      duration: "8-16 weeks",
      description: "Build and deploy AI solutions using proven open-source technologies. Implement robust MLOps practices, automated testing, and continuous integration pipelines that ensure reliable, scalable AI operations.",
      subPhases: [
        {
          name: "Core Development",
          duration: "4-8 weeks",
          description: "Develop core AI models and applications using open-source frameworks and best practices.",
          activities: ["Model development", "Application coding", "Testing implementation", "Performance optimization"]
        },
        {
          name: "MLOps Implementation",
          duration: "2-4 weeks",
          description: "Implement comprehensive MLOps practices for model lifecycle management and deployment.",
          activities: ["CI/CD pipelines", "Model versioning", "Monitoring setup", "Automated testing"]
        },
        {
          name: "Quality Assurance",
          duration: "2-4 weeks",
          description: "Comprehensive testing and validation of AI systems before production deployment.",
          activities: ["Unit testing", "Integration testing", "Performance testing", "Security testing"]
        }
      ],
      deliverables: [
        {
          name: "AI Models & Applications",
          description: "Production-ready AI models and applications"
        },
        {
          name: "MLOps Infrastructure",
          description: "Automated deployment and monitoring systems"
        },
        {
          name: "Testing Framework",
          description: "Comprehensive testing and validation suite"
        },
        {
          name: "Documentation",
          description: "Technical documentation and user guides"
        }
      ],
      metrics: [
        {
          value: "85%+",
          label: "Model Accuracy",
          description: "Performance benchmark"
        },
        {
          value: "24/7",
          label: "Monitoring",
          description: "Continuous oversight"
        },
        {
          value: "Zero",
          label: "Vendor Lock-in",
          description: "Complete independence"
        }
      ]
    },
    {
      title: "Integration & Deployment",
      duration: "4-8 weeks",
      description: "Seamlessly integrate AI solutions with existing systems and deploy to production environments. Implement comprehensive monitoring, alerting, and governance frameworks for enterprise-grade operations.",
      subPhases: [
        {
          name: "System Integration",
          duration: "2-4 weeks",
          description: "Integrate AI solutions with existing enterprise systems and workflows.",
          activities: ["API integration", "Data synchronization", "Workflow automation", "User interface development"]
        },
        {
          name: "Production Deployment",
          duration: "1-2 weeks",
          description: "Deploy AI solutions to production environments with proper monitoring and governance.",
          activities: ["Production deployment", "Monitoring setup", "Alerting configuration", "Performance tuning"]
        },
        {
          name: "User Training",
          duration: "1-2 weeks",
          description: "Train end users and administrators on AI system operation and management.",
          activities: ["User training", "Administrator training", "Documentation delivery", "Support setup"]
        }
      ],
      deliverables: [
        {
          name: "Production System",
          description: "Fully deployed and operational AI solution"
        },
        {
          name: "Monitoring Dashboard",
          description: "Real-time system monitoring and alerting"
        },
        {
          name: "User Training",
          description: "Comprehensive training programs and materials"
        },
        {
          name: "Support Framework",
          description: "Ongoing support and maintenance procedures"
        }
      ],
      metrics: [
        {
          value: "99.5%",
          label: "Deployment Success",
          description: "First-time success rate"
        },
        {
          value: "< 2min",
          label: "Response Time",
          description: "System performance"
        },
        {
          value: "100%",
          label: "User Adoption",
          description: "Training effectiveness"
        }
      ]
    },
    {
      title: "Optimization & Evolution",
      duration: "Ongoing",
      description: "Continuously optimize AI performance, expand capabilities, and evolve solutions based on real-world usage patterns. Implement advanced analytics, A/B testing, and continuous improvement processes.",
      subPhases: [
        {
          name: "Performance Optimization",
          duration: "2-4 weeks",
          description: "Optimize AI system performance based on production usage patterns and feedback.",
          activities: ["Performance analysis", "Model tuning", "Infrastructure optimization", "Cost optimization"]
        },
        {
          name: "Feature Enhancement",
          duration: "Ongoing",
          description: "Continuously enhance AI capabilities based on user feedback and business requirements.",
          activities: ["Feature development", "Model improvements", "User experience enhancement", "Capability expansion"]
        },
        {
          name: "Strategic Evolution",
          duration: "Ongoing",
          description: "Evolve AI strategy and capabilities to meet changing business needs and technology advances.",
          activities: ["Strategy review", "Technology updates", "Capability planning", "Innovation initiatives"]
        }
      ],
      deliverables: [
        {
          name: "Performance Reports",
          description: "Regular performance and optimization reports"
        },
        {
          name: "Enhancement Roadmap",
          description: "Continuous improvement and feature development plan"
        },
        {
          name: "Strategic Updates",
          description: "Quarterly strategy reviews and updates"
        },
        {
          name: "Innovation Pipeline",
          description: "Future capability development and research initiatives"
        }
      ],
      metrics: [
        {
          value: "25%+",
          label: "Performance Gain",
          description: "Continuous improvement"
        },
        {
          value: "Monthly",
          label: "Updates",
          description: "Regular enhancements"
        },
        {
          value: "Future-Ready",
          label: "Evolution",
          description: "Adaptive capabilities"
        }
      ]
    }
  ];

  const caseStudies = [
    {
      companyName: "TechFlow Industries",
      companyLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center",
      industry: "Manufacturing",
      size: "2,500 employees",
      implementationTime: "8 months",
      phase: "Full 5-Phase",
      challenge: "Legacy ERP systems were creating data silos, preventing real-time decision making and predictive maintenance capabilities. Manual quality control processes were causing 15% defect rates and significant production delays.",
      solution: "Implemented comprehensive AI Core Blueprint with open-source ML pipeline for predictive maintenance, computer vision for quality control, and real-time analytics dashboard. Used Apache Kafka for data streaming, TensorFlow for ML models, and Grafana for visualization.",
      metrics: [
        { type: "cost-reduction", icon: "TrendingDown", value: "40%", label: "Infrastructure Costs" },
        { type: "efficiency", icon: "Zap", value: "85%", label: "Defect Reduction" },
        { type: "time-savings", icon: "Clock", value: "6 months", label: "Faster TTM" }
      ],
      technologies: ["TensorFlow", "Apache Kafka", "PostgreSQL", "Docker", "Kubernetes", "Grafana", "OpenCV"],
      testimonial: {
        quote: "The AI Core Blueprint transformed our manufacturing operations completely. We went from reactive maintenance to predictive insights, saving millions in downtime costs.",
        author: "Sarah Chen",
        title: "CTO"
      },
      timeline: [
        { phase: "Assessment & Discovery", duration: "4 weeks" },
        { phase: "Architecture & Design", duration: "6 weeks" },
        { phase: "Implementation", duration: "16 weeks" },
        { phase: "Integration & Deployment", duration: "6 weeks" },
        { phase: "Optimization", duration: "Ongoing" }
      ]
    },
    {
      companyName: "FinanceCore Solutions",
      companyLogo: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop&crop=center",
      industry: "Financial Services",
      size: "850 employees",
      implementationTime: "6 months",
      phase: "Phases 1-4",
      challenge: "Manual fraud detection was missing 25% of fraudulent transactions while flagging 12% of legitimate transactions as false positives. Compliance reporting took 40+ hours per month with high error rates.",
      solution: "Deployed real-time fraud detection using open-source ML models with Apache Spark for stream processing. Implemented automated compliance reporting with natural language processing for document analysis and regulatory change monitoring.",
      metrics: [
        { type: "efficiency", icon: "Shield", value: "95%", label: "Fraud Detection" },
        { type: "cost-reduction", icon: "DollarSign", value: "$2.3M", label: "Annual Savings" },
        { type: "time-savings", icon: "Clock", value: "90%", label: "Faster Reporting" }
      ],
      technologies: ["Apache Spark", "Elasticsearch", "Scikit-learn", "Redis", "MongoDB", "Apache Airflow"],
      testimonial: {
        quote: "Our fraud detection accuracy improved dramatically while reducing false positives. The open-source approach gave us complete control over our risk models.",
        author: "Michael Rodriguez",
        title: "Head of Risk Management"
      }
    }
  ];

  const technicalTestimonials = [
    {
      author: "Dr. Emily Watson",
      title: "Chief Technology Officer",
      company: "DataDriven Corp",
      companySize: "1,200 employees",
      rating: 5,
      implementationTime: "7 months",
      implementationDate: "March 2024",
      quote: "The AI Core Blueprint methodology is the most comprehensive and practical approach to enterprise AI transformation I've encountered. The emphasis on open-source technologies eliminated our vendor lock-in concerns while delivering superior performance and cost savings.",
      metrics: [
        { value: "60%", label: "Cost Reduction" },
        { value: "3x", label: "Performance Gain" },
        { value: "Zero", label: "Vendor Dependencies" }
      ],
      technicalDetails: {
        technologies: ["TensorFlow", "Apache Kafka", "Kubernetes", "PostgreSQL", "Redis", "Grafana"],
        scope: ["ML Pipeline Automation", "Real-time Data Processing", "Predictive Analytics", "Custom Model Development"]
      },
      challenges: [
        {
          challenge: "Legacy System Integration",
          solution: "Implemented microservices architecture with API gateways for seamless integration"
        },
        {
          challenge: "Data Quality Issues",
          solution: "Built automated data validation and cleansing pipelines with Apache Airflow"
        }
      ]
    },
    {
      author: "James Liu",
      title: "VP of Engineering",
      company: "InnovateAI Systems",
      companySize: "450 employees",
      rating: 5,
      implementationTime: "5 months",
      implementationDate: "January 2024",
      quote: "What impressed me most was the methodology's focus on building internal capabilities rather than creating dependencies. Our team now has complete ownership of our AI infrastructure and can innovate at our own pace.",
      metrics: [
        { value: "4x", label: "Deployment Speed" },
        { value: "99.9%", label: "System Uptime" },
        { value: "100%", label: "Team Satisfaction" }
      ],
      technicalDetails: {
        technologies: ["PyTorch", "Apache Spark", "Docker", "MongoDB", "Elasticsearch", "Prometheus"],
        scope: ["Computer Vision Models", "NLP Processing", "Automated Testing", "Performance Monitoring"]
      },
      challenges: [
        {
          challenge: "Scalability Requirements",
          solution: "Implemented auto-scaling Kubernetes clusters with horizontal pod autoscaling"
        },
        {
          challenge: "Model Versioning",
          solution: "Created comprehensive MLOps pipeline with automated model versioning and rollback capabilities"
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>AI Core Blueprint - Comprehensive Enterprise AI Methodology | AI Clean Core</title>
        <meta name="description" content="Discover our flagship 5-phase AI transformation methodology. Download the complete blueprint with implementation templates, ROI frameworks, and proven strategies for enterprise AI success." />
        <meta name="keywords" content="AI methodology, enterprise AI transformation, AI implementation blueprint, open source AI, AI strategy framework" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-br from-brand-primary via-brand-primary to-brand-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
                <Icon name="Award" size={16} className="mr-2" />
                Flagship Methodology • Proven by 200+ Enterprises
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                AI Core Blueprint
                <span className="block text-brand-secondary">Methodology</span>
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed">
                The definitive 5-phase framework for enterprise AI transformation. 
                Build internal AI capabilities, eliminate vendor dependencies, and achieve 
                sustainable competitive advantage through open-source excellence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-white text-brand-primary hover:bg-gray-100"
                  iconName="Download"
                  iconPosition="left"
                >
                  Download Complete Blueprint
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-brand-primary"
                  iconName="Play"
                  iconPosition="left"
                >
                  Watch Methodology Overview
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Methodology Overview */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                Five-Phase Transformation Framework
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                Our proven methodology transforms organizations from AI-curious to AI-native, 
                building sustainable competitive advantages through strategic open-source adoption.
              </p>
            </div>

            {/* Interactive Phase Navigation */}
            <div className="mb-12">
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {methodologyPhases?.map((phase, index) => (
                  <button
                    key={index}
                    onClick={() => setActivePhase(index)}
                    className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                      activePhase === index
                        ? 'bg-brand-primary text-white shadow-executive'
                        : 'bg-gray-100 text-text-primary hover:bg-gray-200'
                    }`}
                  >
                    Phase {index + 1}: {phase?.title?.split(' ')?.[0]}
                  </button>
                ))}
              </div>

              {/* Progress Indicator */}
              <div className="relative">
                <div className="flex justify-between items-center mb-4">
                  {methodologyPhases?.map((_, index) => (
                    <div
                      key={index}
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                        index <= activePhase
                          ? 'bg-brand-primary text-white' :'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {index + 1}
                    </div>
                  ))}
                </div>
                <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 -z-10">
                  <div 
                    className="h-full bg-brand-primary transition-all duration-500"
                    style={{ width: `${(activePhase / (methodologyPhases?.length - 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Phase Details */}
            <div className="space-y-8">
              {methodologyPhases?.map((phase, index) => (
                <MethodologyPhase
                  key={index}
                  phase={phase}
                  index={index}
                  isActive={activePhase === index}
                  onToggle={() => setActivePhase(activePhase === index ? -1 : index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Configurator */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                Customize Your Blueprint
              </h2>
              <p className="text-xl text-text-secondary leading-relaxed">
                Every organization is unique. Configure the AI Core Blueprint based on your 
                specific context, industry, and transformation goals.
              </p>
            </div>
            <MethodologyConfigurator />
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                Real-World Transformations
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                See how leading organizations have successfully implemented the AI Core Blueprint 
                to achieve measurable business outcomes and competitive advantages.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {caseStudies?.map((caseStudy, index) => (
                <CaseStudyCallout
                  key={index}
                  caseStudy={caseStudy}
                  variant={index === 0 ? 'featured' : 'default'}
                />
              ))}
            </div>

            <div className="text-center">
              <Link to="/case-studies-proof">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  View All Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Technical Testimonials */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                Technical Leader Validation
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                CTOs and engineering leaders share their experiences implementing the AI Core Blueprint 
                and achieving technical excellence with open-source AI solutions.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {technicalTestimonials?.map((testimonial, index) => (
                <TechnicalTestimonial
                  key={index}
                  testimonial={testimonial}
                  layout="card"
                />
              ))}
            </div>

            <div className="bg-white rounded-xl shadow-executive p-8">
              <div className="text-center mb-8">
                <Icon name="Users" size={48} className="text-brand-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-text-primary mb-2">Join the Technical Community</h3>
                <p className="text-text-secondary">
                  Connect with 500+ technical leaders who have successfully implemented the AI Core Blueprint
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-brand-primary">500+</div>
                  <div className="text-sm text-text-secondary">Technical Leaders</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-brand-primary">200+</div>
                  <div className="text-sm text-text-secondary">Successful Implementations</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-brand-primary">95%</div>
                  <div className="text-sm text-text-secondary">Satisfaction Rate</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blueprint Download */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                Get the Complete Blueprint
              </h2>
              <p className="text-xl text-text-secondary leading-relaxed">
                Download our comprehensive 47-page methodology guide with implementation templates, 
                ROI frameworks, and proven strategies used by Fortune 500 companies.
              </p>
            </div>
            <BlueprintDownload />
          </div>
        </section>

        {/* Next Steps */}
        <section className="py-20 bg-gradient-to-br from-brand-primary to-brand-secondary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Organization?
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              The AI Core Blueprint has guided 200+ organizations to AI independence. 
              Start your transformation journey with a strategic consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-started">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-white text-brand-primary hover:bg-gray-100"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Schedule Strategic Consultation
                </Button>
              </Link>
              <Link to="/services-implementation">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-brand-primary"
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Explore Implementation Services
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-xl font-bold">AI Clean Core</div>
                    <div className="text-sm text-gray-400">Enterprise AI Independence</div>
                  </div>
                </div>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Empowering organizations to achieve AI independence through open-source excellence 
                  and strategic transformation methodologies.
                </p>
                <div className="flex space-x-4">
                  <Icon name="Twitter" size={20} className="text-gray-400 hover:text-white cursor-pointer" />
                  <Icon name="Linkedin" size={20} className="text-gray-400 hover:text-white cursor-pointer" />
                  <Icon name="Github" size={20} className="text-gray-400 hover:text-white cursor-pointer" />
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Resources</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/resources-hub" className="hover:text-white">Resource Hub</Link></li>
                  <li><Link to="/case-studies-proof" className="hover:text-white">Case Studies</Link></li>
                  <li><Link to="/open-source-advantage" className="hover:text-white">Open Source Guide</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/about-ai-clean-core" className="hover:text-white">About</Link></li>
                  <li><Link to="/services-implementation" className="hover:text-white">Services</Link></li>
                  <li><Link to="/get-started" className="hover:text-white">Get Started</Link></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; {new Date()?.getFullYear()} AI Clean Core. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AICoreBlueprint;